/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    PROFILE_MODAL_IMPLEMENTATION
 * Date:         2025-12-13
 * Modified By:  Cursor
 *
 * Description:
 * Reusable profile content component that displays company and user profiles
 * with tab switching. Used in both Setup review and seller profile modal.
 *
 * Related Screens / Modules:
 * - client/src/components/setup/ReviewProfileForm.tsx (original source)
 * - client/src/components/ProfileModal.tsx (modal wrapper)
 */

import React, { useState } from "react";
import { Row, Col, Card, Image } from "react-bootstrap";
import { User, Hours } from "../interfaces/User";
import "./ProfileModalContent.scss";

type ReviewTabKey = "company" | "profile";

interface ProfileModalContentProps {
  profile: Partial<User>;
  initialTab?: ReviewTabKey;
  showBroadcastSignature?: boolean; // Hide in view-only modal
}

const ProfileModalContent: React.FC<ProfileModalContentProps> = ({
  profile,
  initialTab = "company",
  showBroadcastSignature = false,
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [activeTab, setActiveTab] = useState<ReviewTabKey>(initialTab);

  // [2025-12-13] Profile Modal: Utility functions extracted from ReviewProfileForm
  const getImageSrc = (img?: { data: string; contentType: string }) => {
    if (!img?.data) return "";
    // Handle both cases: data already has "data:" prefix or needs to be added
    return img.data.startsWith("data:") ? img.data : `data:${img.contentType};base64,${img.data}`;
  };

  const formatAddress = () => {
    const parts = [
      profile.address1,
      profile.address2,
      profile.city,
      profile.state,
      profile.zip,
      profile.country,
    ].filter(Boolean);
    return parts.join(", ") || "Not provided";
  };

  const formatHours = (hours?: Hours): string => {
    if (!hours) return "Not provided";

    const { fromDay, toDay, from, to, timeZone } = hours;

    // Check if hours are complete (all fields present)
    const hasAllHours = !!(fromDay && toDay && from && to && timeZone);

    if (!hasAllHours) {
      return "Not provided";
    }

    // Format day range
    const dayPart = fromDay === toDay ? fromDay : `${fromDay} – ${toDay}`;

    // Format time range
    const timePart = `${from} – ${to}`;

    // Format timezone (show only the city name if it's a standard IANA format)
    let tzPart = timeZone;
    if (timeZone && timeZone.includes("/")) {
      // Extract city name from IANA format (e.g., "America/New_York" -> "New York")
      const parts = timeZone.split("/");
      tzPart = parts[parts.length - 1].replace(/_/g, " ");
    }

    return `${dayPart}, ${timePart}, ${tzPart}`;
  };

  const isCompanyTab = activeTab === "company";

  return (
    <div className="profile-modal-content">
      {/* Tab Navigation */}
      {/* <Nav
        variant="tabs"
        className="review-tabs mb-4"
        activeKey={activeTab}
        onSelect={(k) => setActiveTab((k as ReviewTabKey) || "company")}
      >
        <Nav.Item className="review-tab-item">
          <Nav.Link eventKey="company" className="review-tab-link">
            Company profile
          </Nav.Link>
        </Nav.Item>
        <Nav.Item className="review-tab-item">
          <Nav.Link eventKey="profile" className="review-tab-link">
            Main contact profile
          </Nav.Link>
        </Nav.Item>
      </Nav> */}

      {/* Tab Content */}
      {isCompanyTab ? (
        // Company Profile Tab
        <Row>
          <Col md={8}>
            <Card className="mb-3">
              <Card.Body>
                {/* Company Logo */}
                {profile.companyLogos?.[0] && (
                  <div className="mb-3 text-center">
                    <Image
                      src={getImageSrc(profile.companyLogos[0])}
                      alt="Company Logo"
                      style={{ maxWidth: "200px", maxHeight: "200px" }}
                      rounded
                    />
                  </div>
                )}

                {/* Company Name */}
                <h3 className="mb-3">{profile.companyName || "Not provided"}</h3>

                {/* About Company */}
                <div className="mb-3">
                  <h5>About</h5>
                  <p className="text-muted">{(profile as any)?.aboutCompany || "Not provided"}</p>
                </div>

                {/* Services */}
                <div className="mb-3">
                  <h5>Services</h5>
                  <p className="text-muted">{profile.services || "Not provided"}</p>
                </div>

                {/* Certifications */}
                {profile.certifications && profile.certifications.length > 0 && (
                  <div className="mb-3">
                    <h5>Certifications</h5>
                    <div className="d-flex flex-wrap gap-2">
                      {profile.certifications.map((cert, idx) => (
                        <Image
                          key={idx}
                          src={getImageSrc(cert)}
                          alt={`Certification ${idx + 1}`}
                          style={{ maxWidth: "100px", maxHeight: "100px" }}
                          rounded
                        />
                      ))}
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            {/* Company Details Card */}
            <Card className="mb-3">
              <Card.Body>
                <h5>Company Details</h5>
                <div className="mb-2">
                  <strong>Website:</strong>
                  <br />
                  {profile.website ? (
                    <a href={profile.website} target="_blank" rel="noopener noreferrer">
                      {profile.website}
                    </a>
                  ) : (
                    <span className="text-muted">Not provided</span>
                  )}
                </div>
                <div className="mb-2">
                  <strong>Email:</strong>
                  <br />
                  <span className="text-muted">{profile.companyEmail || "Not provided"}</span>
                </div>
                <div className="mb-2">
                  <strong>Phone:</strong>
                  <br />
                  <span className="text-muted">{profile.telCompany || "Not provided"}</span>
                </div>
                <div className="mb-2">
                  <strong>Address:</strong>
                  <br />
                  <span className="text-muted">{formatAddress()}</span>
                </div>
                <div className="mb-2">
                  <strong>Hours:</strong>
                  <br />
                  <span className="text-muted">{formatHours(profile.hours)}</span>
                </div>
              </Card.Body>
            </Card>

            {/* Social Media Card */}
            <Card>
              <Card.Body>
                <h5>Social Media</h5>
                {profile.linkedIn && (
                  <div className="mb-2">
                    <strong>LinkedIn:</strong>
                    <br />
                    <a href={profile.linkedIn} target="_blank" rel="noopener noreferrer">
                      {profile.linkedIn}
                    </a>
                  </div>
                )}
                {profile.x && (
                  <div className="mb-2">
                    <strong>X (Twitter):</strong>
                    <br />
                    <a href={profile.x} target="_blank" rel="noopener noreferrer">
                      {profile.x}
                    </a>
                  </div>
                )}
                {profile.instagram && (
                  <div className="mb-2">
                    <strong>Instagram:</strong>
                    <br />
                    <a href={profile.instagram} target="_blank" rel="noopener noreferrer">
                      {profile.instagram}
                    </a>
                  </div>
                )}
                {profile.facebook && (
                  <div className="mb-2">
                    <strong>Facebook:</strong>
                    <br />
                    <a href={profile.facebook} target="_blank" rel="noopener noreferrer">
                      {profile.facebook}
                    </a>
                  </div>
                )}
                {!profile.linkedIn && !profile.x && !profile.instagram && !profile.facebook && (
                  <p className="text-muted">Not provided</p>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      ) : (
        // User Profile Tab
        <Row>
          <Col md={8}>
            <Card className="mb-3">
              <Card.Body>
                {/* User Avatar */}
                {profile.profileImages && (
                  <div className="mb-3 text-center">
                    <Image
                      src={getImageSrc(profile.profileImages)}
                      alt="Profile"
                      roundedCircle
                      style={{ width: "120px", height: "120px" }}
                    />
                  </div>
                )}

                {/* User Name */}
                <h3 className="mb-3">
                  {profile.firstName} {profile.lastName}
                </h3>

                {/* Job Title */}
                {profile.jobTitle && <p className="text-muted mb-3">{profile.jobTitle}</p>}

                {/* Bio */}
                <div className="mb-3">
                  <h5>Bio</h5>
                  <p className="text-muted">{profile.bio || "Not provided"}</p>
                </div>

                {/* Broadcast Signature - Only show in setup, not in view modal */}
                {showBroadcastSignature && (
                  <div className="mb-3">
                    <h5>Broadcast Signature</h5>
                    {/* [2026-02-06] Added consistent font styling to match TinyMCE editor */}
                    <div
                      className="border rounded p-3 bg-light u-P3-14-medium"
                      dangerouslySetInnerHTML={{
                        __html: profile.broadcastSignatureHtml || "Not set",
                      }}
                    />
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            {/* Contact Info Card */}
            <Card className="mb-3">
              <Card.Body>
                <h5>Contact Information</h5>
                <div className="mb-2">
                  <strong>Email:</strong>
                  <br />
                  <span className="text-muted">{profile.email || "Not provided"}</span>
                </div>
                <div className="mb-2">
                  <strong>Phone:</strong>
                  <br />
                  <span className="text-muted">{profile.tel || "Not provided"}</span>
                </div>
              </Card.Body>
            </Card>

            {/* Social Media Card */}
            <Card>
              <Card.Body>
                <h5>Social Media</h5>
                {profile.linkedIn && (
                  <div className="mb-2">
                    <strong>LinkedIn:</strong>
                    <br />
                    <a href={profile.linkedIn} target="_blank" rel="noopener noreferrer">
                      {profile.linkedIn}
                    </a>
                  </div>
                )}
                {profile.x && (
                  <div className="mb-2">
                    <strong>X (Twitter):</strong>
                    <br />
                    <a href={profile.x} target="_blank" rel="noopener noreferrer">
                      {profile.x}
                    </a>
                  </div>
                )}
                {profile.instagram && (
                  <div className="mb-2">
                    <strong>Instagram:</strong>
                    <br />
                    <a href={profile.instagram} target="_blank" rel="noopener noreferrer">
                      {profile.instagram}
                    </a>
                  </div>
                )}
                {profile.facebook && (
                  <div className="mb-2">
                    <strong>Facebook:</strong>
                    <br />
                    <a href={profile.facebook} target="_blank" rel="noopener noreferrer">
                      {profile.facebook}
                    </a>
                  </div>
                )}
                {!profile.linkedIn && !profile.x && !profile.instagram && !profile.facebook && (
                  <p className="text-muted">Not provided</p>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default ProfileModalContent;
